import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Star } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { submitReview } from '@/services/ratingService';

interface Question {
  id: number;
  text: string;
}

const questions: Question[] = [
  { id: 1, text: "Was your driver on time?" },
  { id: 2, text: "Was your driver friendly and professional?" },
  { id: 3, text: "Did you get picked up and dropped off in the right places?" },
  { id: 4, text: "How was the quality of the vehicle you were in?" },
  { id: 5, text: "Would you use Disco again?" }
];

const StarRating: React.FC<{
  questionId: number;
  rating: number;
  onRatingChange: (questionId: number, rating: number) => void;
}> = ({ questionId, rating, onRatingChange }) => {
  return (
    <div className="flex gap-1 md:gap-2">
      <svg width="0" height="0">
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#2BFF00" />
            <stop offset="100%" stopColor="#31FFE7" />
          </linearGradient>
        </defs>
      </svg>
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          onClick={() => onRatingChange(questionId, star)}
          className="transition-all duration-200 hover:scale-110"
          aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
        >
          <Star
            size={32}
            className="transition-all duration-200"
            style={{
              fill: star <= rating 
                ? 'url(#gradient)' 
                : 'transparent',
              stroke: star <= rating ? 'none' : 'white'
            }}
          />
        </button>
      ))}
    </div>
  );
};

const Review: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [ratings, setRatings] = useState<Record<number, number>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRatingChange = (questionId: number, rating: number) => {
    setRatings(prev => ({
      ...prev,
      [questionId]: rating
    }));
  };

  const allQuestionsAnswered = questions.every(q => ratings[q.id] > 0);
  
  const averageRating = allQuestionsAnswered 
    ? Object.values(ratings).reduce((sum, rating) => sum + rating, 0) / questions.length
    : 0;

  const handleSubmit = async () => {
    const tripId = searchParams.get('trip_id');
    
    if (!tripId) {
      toast({
        title: "Error",
        description: "No trip ID found. Please use the link provided.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      await submitReview({
        trip_id: tripId,
        ratings,
        average_rating: averageRating,
      });

      toast({
        title: "Review submitted!",
        description: "Thank you for your feedback.",
      });

      // Navigate back to dashboard or trips after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit review. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Rate Your <span className="gradient-text">Disco</span> Experience
          </h1>
          <p className="text-muted-foreground text-lg">
            Your feedback helps us improve our service
          </p>
        </div>

        {/* Questions */}
        <div className="space-y-6 md:space-y-8 mb-8">
          {questions.map((question) => (
            <Card key={question.id} className="p-6 md:p-8 bg-card border-border">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg md:text-xl font-medium text-foreground mb-2 md:mb-0">
                    {question.text}
                  </h3>
                </div>
                <div className="flex justify-center md:justify-end">
                  <StarRating
                    questionId={question.id}
                    rating={ratings[question.id] || 0}
                    onRatingChange={handleRatingChange}
                  />
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Average Rating Display */}
        {allQuestionsAnswered && (
          <Card className="p-6 md:p-8 bg-card border-border mb-8">
            <div className="text-center">
              <h3 className="text-xl md:text-2xl font-bold mb-2">
                Your Overall Rating
              </h3>
              <div className="text-4xl md:text-5xl font-bold gradient-text mb-4">
                {averageRating.toFixed(1)} ⭐
              </div>
              <p className="text-muted-foreground">
                Thank you for your honest feedback!
              </p>
            </div>
          </Card>
        )}

        {/* Submit Button */}
        <div className="text-center">
          <Button
            onClick={handleSubmit}
            disabled={!allQuestionsAnswered || isSubmitting}
            className={`px-8 py-3 text-lg font-medium rounded-lg transition-all duration-200 ${
              allQuestionsAnswered && !isSubmitting
                ? 'bg-primary hover:bg-primary/90 text-primary-foreground'
                : 'bg-muted text-muted-foreground cursor-not-allowed'
            }`}
          >
            {isSubmitting 
              ? 'Submitting...' 
              : allQuestionsAnswered 
                ? 'Submit Review' 
                : 'Please answer all questions'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Review;